import React from 'react';
import { INR } from '../../core/helpers';
import { invoiceLogoSrc, BRAND_DISPLAY_NAME } from './invoicePrintStyles';

function InvoicePrintView({ invoice, org, client, subtotal, cgst, sgst, igst, isSameState, payable, isClient }) {
  const gstRate = Number(org.gstRate) || 18;
  const logoSrc = invoiceLogoSrc(org);
  const balance = isClient && payable?.hasInstallments ? payable.amount : (invoice.pending || 0);
  const balanceLabel = isClient && payable?.hasInstallments ? 'Next instalment due' : 'Balance Due';

  return (
    <div id="invoice-print-area" className="inv-print">
      <div className="inv-print-top">
        <div className="inv-print-brand">
          <img src={logoSrc} alt={BRAND_DISPLAY_NAME} className="inv-print-logo" onError={(e) => { e.target.style.display = 'none'; }} />
          <div className="inv-print-brand-name">{BRAND_DISPLAY_NAME}</div>
        </div>
        <div className="inv-print-company">
          <div className="inv-print-company-name">{org.name}</div>
          {org.address && <div>{org.address}</div>}
          <div>GSTIN: {org.gstin || '—'} · PAN: {org.pan || '—'}</div>
          {(org.email || org.phone) && (
            <div>{[org.email, org.phone].filter(Boolean).join(' · ')}</div>
          )}
        </div>
      </div>

      <div className="inv-print-title-row">
        <span className="inv-print-line" />
        <span className="inv-print-title">TAX INVOICE</span>
        <span className="inv-print-line" />
      </div>
      <div className="inv-print-invno">Invoice# <strong>{invoice.invoiceNo}</strong></div>

      <div className="inv-print-grid">
        <div className="inv-print-box">
          <div className="inv-print-lbl">Bill To</div>
          <div className="inv-print-bill-name">{client?.contactName || invoice.clientName}</div>
          <div className="inv-print-bill-sub">{client?.name || ''}</div>
          {client?.gst && <div className="inv-print-bill-meta">GSTIN: {client.gst}</div>}
          {client?.state && <div className="inv-print-bill-meta">State: {client.state}</div>}
        </div>
        <div className="inv-print-box">
          <div className="inv-print-lbl">Payment Details</div>
          <div className="inv-print-pay">
            <div><strong>{org.bankName}</strong></div>
            <div>A/c: {org.accountNo} · IFSC: {org.ifsc}</div>
            <div style={{ color: '#92400E', fontWeight: 600 }}>UPI: {org.upi}</div>
          </div>
        </div>
      </div>

      <table className="inv-print-meta">
        <thead>
          <tr>
            <th>Invoice Date</th>
            <th>Due Date</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{invoice.date}</td>
            <td>{invoice.dueDate || '—'}</td>
          </tr>
        </tbody>
      </table>

      <div className="inv-print-items-wrap">
        <table className="inv-print-items">
          <thead>
            <tr>
              <th style={{ width: 32 }}>#</th>
              <th>Item &amp; Description</th>
              <th style={{ width: 58, textAlign: 'center' }}>SAC</th>
              <th style={{ width: 44, textAlign: 'center' }}>Qty</th>
              <th style={{ width: 80, textAlign: 'right' }}>Rate (₹)</th>
              <th style={{ width: 80, textAlign: 'right' }}>Taxable (₹)</th>
              <th style={{ width: 50, textAlign: 'center' }}>GST</th>
              <th style={{ width: 88, textAlign: 'right' }}>Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.lineItems || []).map((li, i) => {
              const base = (li.unitPrice || 0) * (li.qty || 1);
              const amt = li.gst ? base * (1 + gstRate / 100) : base;
              const sac = li.sac || org.sac || '998211';
              return (
                <tr key={li.id || i}>
                  <td style={{ color: '#6B7280' }}>{i + 1}</td>
                  <td>
                    <div className="inv-print-item-name">{li.name}</div>
                    {li.type && <div className="inv-print-item-sub">{li.type}</div>}
                  </td>
                  <td style={{ textAlign: 'center', fontFamily: 'monospace', fontSize: 12 }}>{sac}</td>
                  <td style={{ textAlign: 'center', fontWeight: 600 }}>{li.qty || 1}</td>
                  <td style={{ textAlign: 'right' }}>{INR(li.unitPrice || 0)}</td>
                  <td style={{ textAlign: 'right', color: '#6B7280' }}>{INR(base)}</td>
                  <td style={{ textAlign: 'center' }}>{li.gst ? `${gstRate}%` : 'Nil'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{INR(amt)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td />
              <td className="inv-print-tot-label">Sub Total</td>
              <td colSpan={5} />
              <td className="inv-print-tot-amt">{INR(subtotal)}</td>
            </tr>
            {isSameState ? (
              <>
                <tr>
                  <td />
                  <td className="inv-print-tot-label">CGST @ {gstRate / 2}%</td>
                  <td colSpan={5} />
                  <td className="inv-print-tot-amt">{INR(cgst)}</td>
                </tr>
                <tr>
                  <td />
                  <td className="inv-print-tot-label">SGST @ {gstRate / 2}%</td>
                  <td colSpan={5} />
                  <td className="inv-print-tot-amt">{INR(sgst)}</td>
                </tr>
              </>
            ) : (
              <tr>
                <td />
                <td className="inv-print-tot-label">IGST @ {gstRate}%</td>
                <td colSpan={5} />
                <td className="inv-print-tot-amt">{INR(igst)}</td>
              </tr>
            )}
            <tr className="inv-print-tot-total">
              <td />
              <td className="inv-print-tot-label">Total</td>
              <td colSpan={5} />
              <td className="inv-print-tot-amt">{INR(invoice.total || 0)}</td>
            </tr>
            <tr className="inv-print-tot-paid">
              <td />
              <td className="inv-print-tot-label">Amount Paid</td>
              <td colSpan={5} />
              <td className="inv-print-tot-amt">{INR(invoice.paid || 0)}</td>
            </tr>
            <tr className={balance > 0 ? 'inv-print-tot-due' : 'inv-print-tot-ok'}>
              <td />
              <td className="inv-print-tot-label">{balanceLabel}</td>
              <td colSpan={5} />
              <td className="inv-print-tot-amt">{INR(balance)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="inv-print-digital">
        <div className="inv-print-digital-badge">Digitally Generated &amp; Approved</div>
        <div className="inv-print-note">This invoice is computer-generated and is valid without a physical signature or stamp.</div>
      </div>

      {balance > 0 && invoice.dueDate && (
        <div className="inv-print-alert">Please make payment by <strong>{invoice.dueDate}</strong>.</div>
      )}
    </div>
  );
}

export { InvoicePrintView };
